/**
 * YouTube Analytics Component
 * 
 * Displays video metrics, analytics, and performance data for uploaded videos.
 * 
 * Features:
 * - List of uploaded videos with metrics
 * - Individual video analytics
 * - Sorting and filtering
 * - Pagination
 * - Real-time data refresh
 * - Export capabilities
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  getChannelVideos,
  getVideoMetrics,
  formatNumber,
  formatDuration,
  checkYouTubeConnection
} from '../services/youtubeService';

// Icon components
const ViewIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8 3C4.5 3 1.5 5.5 0 8c1.5 2.5 4.5 5 8 5s6.5-2.5 8-5c-1.5-2.5-4.5-5-8-5zm0 8a3 3 0 110-6 3 3 0 010 6zm0-5a2 2 0 100 4 2 2 0 000-4z"/>
  </svg>
);

const LikeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M8.864 2.908a.75.75 0 00-1.328 0L5.876 7.132 1.416 7.89a.75.75 0 00-.416 1.28l3.234 3.143-.764 4.442a.75.75 0 001.088.79L8 15.347l3.442 1.798a.75.75 0 001.088-.79l-.764-4.442 3.234-3.143a.75.75 0 00-.416-1.28l-4.46-.758-1.66-4.224z"/>
  </svg>
);

const CommentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M2.5 1A1.5 1.5 0 001 2.5v8A1.5 1.5 0 002.5 12h9.793l2.854 2.854A.5.5 0 0016 14.5v-12A1.5 1.5 0 0014.5 1h-12z"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <path d="M3.5 0a.5.5 0 01.5.5V1h8V.5a.5.5 0 011 0V1h1a2 2 0 012 2v11a2 2 0 01-2 2H2a2 2 0 01-2-2V3a2 2 0 012-2h1V.5a.5.5 0 01.5-.5zM1 4v10a1 1 0 001 1h12a1 1 0 001-1V4H1z"/>
  </svg>
);

function YouTubeAnalytics() {
  const { tenant } = useParams();

  // State management
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoMetrics, setVideoMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [sortBy, setSortBy] = useState('publishedAt'); // publishedAt, viewCount, likeCount
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [searchQuery, setSearchQuery] = useState('');

  // Get tenant ID
  const getTenantId = useCallback(() => {
    return Number(tenant) || Number(localStorage.getItem('tenantId')) || 1;
  }, [tenant]);

  /**
   * Load channel videos
   */
  const loadVideos = useCallback(async (pageToken = null, append = false, forceRefresh = false) => {
    try {
      if (!append) {
        setLoading(true);
      }
      setError(null);

      const tenantId = getTenantId();
      
      // Check connection first
      const connection = await checkYouTubeConnection(tenantId, false);
      setIsConnected(connection.connected);

      if (!connection.connected) {
        setVideos([]);
        return;
      }

      // Fetch videos — a forced refresh must bypass getChannelVideos' own
      // 2-minute cache, or clicking "Refresh" can silently keep showing
      // stale data.
      const result = await getChannelVideos(tenantId, 50, pageToken, !pageToken && !forceRefresh);
      
      if (result.error) {
        throw new Error(result.error);
      }

      if (append) {
        setVideos(prev => [...prev, ...result.videos]);
      } else {
        setVideos(result.videos);
      }

      setNextPageToken(result.nextPageToken);
      setHasMore(!!result.nextPageToken);
    } catch (err) {
      console.error('Error loading videos:', err);
      setError(err.message || 'Failed to load videos');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  }, [getTenantId]);

  /**
   * Load more videos (pagination)
   */
  const loadMore = () => {
    if (nextPageToken && !loading) {
      loadVideos(nextPageToken, true);
    }
  };

  /**
   * Load detailed metrics for a specific video
   */
  const loadVideoMetrics = useCallback(async (videoId) => {
    try {
      setLoadingMetrics(true);
      setError(null);

      const tenantId = getTenantId();
      const metrics = await getVideoMetrics(tenantId, videoId, false);
      
      if (metrics.error) {
        throw new Error(metrics.error);
      }

      setVideoMetrics(metrics);
    } catch (err) {
      console.error('Error loading video metrics:', err);
      setError(err.message || 'Failed to load video metrics');
    } finally {
      setLoadingMetrics(false);
    }
  }, [getTenantId]);

  /**
   * Handle video selection
   */
  const handleVideoSelect = (video) => {
    setSelectedVideo(video);
    loadVideoMetrics(video.id);
  };

  /**
   * Close video details
   */
  const handleCloseDetails = () => {
    setSelectedVideo(null);
    setVideoMetrics(null);
  };

  /**
   * Initial load
   */
  useEffect(() => {
    loadVideos();
  }, [loadVideos]);

  /**
   * Sort videos
   */
  const sortedVideos = React.useMemo(() => {
    let filtered = videos;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = videos.filter(video => 
        video.title?.toLowerCase().includes(query) ||
        video.description?.toLowerCase().includes(query)
      );
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle date sorting
      if (sortBy === 'publishedAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });
  }, [videos, sortBy, sortOrder, searchQuery]);

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Not connected state
  if (!loading && !isConnected) {
    return (
      <div className="youtube-analytics-container">
        <div className="analytics-header">
          <h2>YouTube Analytics</h2>
        </div>
        <div className="not-connected">
          <div className="not-connected-icon">📊</div>
          <h3>YouTube Not Connected</h3>
          <p>Connect your YouTube account to view video analytics and metrics.</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="youtube-analytics-container">
        <div className="analytics-header">
          <h2>YouTube Analytics</h2>
        </div>
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading videos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="youtube-analytics-container">
      {/* Header */}
      <div className="analytics-header">
        <h2>YouTube Analytics</h2>
        <button 
          className="btn btn-secondary"
          onClick={() => loadVideos(null, false, true)}
          disabled={loading}
        >
          🔄 Refresh
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
          <button 
            className="alert-close" 
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Controls */}
      <div className="analytics-controls">
        {/* Search */}
        <div className="search-box">
          <input
            type="text"
            placeholder="Search videos..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Sort controls */}
        <div className="sort-controls">
          <label>Sort by:</label>
          <select 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="publishedAt">Date Published</option>
            <option value="viewCount">Views</option>
            <option value="likeCount">Likes</option>
            <option value="commentCount">Comments</option>
          </select>
          <button
            className="btn btn-icon"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {/* Videos list */}
      {sortedVideos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎥</div>
          <h3>No videos found</h3>
          <p>
            {searchQuery 
              ? 'No videos match your search criteria.' 
              : 'You haven\'t uploaded any videos yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="videos-grid">
            {sortedVideos.map((video) => (
              <div 
                key={video.id} 
                className="video-card"
                onClick={() => handleVideoSelect(video)}
              >
                {/* Thumbnail */}
                <div className="video-thumbnail">
                  {video.thumbnails?.medium?.url ? (
                    <img 
                      src={video.thumbnails.medium.url} 
                      alt={video.title}
                    />
                  ) : (
                    <div className="no-thumbnail">No Thumbnail</div>
                  )}
                  {video.duration && (
                    <span className="video-duration">
                      {formatDuration(video.duration)}
                    </span>
                  )}
                  <span className={`privacy-badge ${video.privacyStatus}`}>
                    {video.privacyStatus}
                  </span>
                </div>

                {/* Video info */}
                <div className="video-info">
                  <h3 className="video-title">{video.title}</h3>
                  <div className="video-date">
                    <CalendarIcon />
                    {formatDate(video.publishedAt)}
                  </div>
                  <div className="video-stats">
                    <div className="stat">
                      <ViewIcon />
                      <span>{formatNumber(video.viewCount)}</span>
                    </div>
                    <div className="stat">
                      <LikeIcon />
                      <span>{formatNumber(video.likeCount)}</span>
                    </div>
                    <div className="stat">
                      <CommentIcon />
                      <span>{formatNumber(video.commentCount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Load more button */}
          {hasMore && (
            <div className="load-more-container">
              <button
                className="btn btn-secondary"
                onClick={loadMore}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load More Videos'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Video details modal */}
      {selectedVideo && (
        <div className="modal-overlay" onClick={handleCloseDetails}>
          <div className="modal-content yt-video-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Video Details</h3>
              <button 
                className="modal-close" 
                onClick={handleCloseDetails}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {loadingMetrics ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading metrics...</p>
                </div>
              ) : videoMetrics ? (
                <>
                  {/* Video preview */}
                  <div className="video-preview">
                    <iframe
                      width="100%"
                      height="315"
                      src={`https://www.youtube.com/embed/${selectedVideo.id}`}
                      title={videoMetrics.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>

                  {/* Metrics */}
                  <div className="metrics-grid">
                    <div className="metric-card">
                      <div className="metric-icon views">
                        <ViewIcon />
                      </div>
                      <div className="metric-details">
                        <span className="metric-label">Views</span>
                        <span className="metric-value">
                          {formatNumber(videoMetrics.viewCount)}
                        </span>
                      </div>
                    </div>

                    <div className="metric-card">
                      <div className="metric-icon likes">
                        <LikeIcon />
                      </div>
                      <div className="metric-details">
                        <span className="metric-label">Likes</span>
                        <span className="metric-value">
                          {formatNumber(videoMetrics.likeCount)}
                        </span>
                      </div>
                    </div>

                    <div className="metric-card">
                      <div className="metric-icon comments">
                        <CommentIcon />
                      </div>
                      <div className="metric-details">
                        <span className="metric-label">Comments</span>
                        <span className="metric-value">
                          {formatNumber(videoMetrics.commentCount)}
                        </span>
                      </div>
                    </div>

                    <div className="metric-card">
                      <div className="metric-icon favorites">
                        ⭐
                      </div>
                      <div className="metric-details">
                        <span className="metric-label">Favorites</span>
                        <span className="metric-value">
                          {formatNumber(videoMetrics.favoriteCount)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Video details */}
                  <div className="video-details-section">
                    <h4>Video Information</h4>
                    <div className="detail-row">
                      <span className="detail-label">Status:</span>
                      <span className={`privacy-badge ${videoMetrics.privacyStatus}`}>
                        {videoMetrics.privacyStatus}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Published:</span>
                      <span>{formatDate(videoMetrics.publishedAt)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Duration:</span>
                      <span>{formatDuration(videoMetrics.duration)}</span>
                    </div>
                    {videoMetrics.embeddable !== undefined && (
                      <div className="detail-row">
                        <span className="detail-label">Embeddable:</span>
                        <span>{videoMetrics.embeddable ? 'Yes' : 'No'}</span>
                      </div>
                    )}
                    {videoMetrics.license && (
                      <div className="detail-row">
                        <span className="detail-label">License:</span>
                        <span>{videoMetrics.license}</span>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {videoMetrics.description && (
                    <div className="video-details-section">
                      <h4>Description</h4>
                      <p className="video-description">
                        {videoMetrics.description}
                      </p>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="modal-actions">
                    <a
                      href={`https://www.youtube.com/watch?v=${selectedVideo.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      View on YouTube
                    </a>
                    <a
                      href={`https://studio.youtube.com/video/${selectedVideo.id}/edit`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                    >
                      Edit in Studio
                    </a>
                  </div>
                </>
              ) : (
                <div className="error-state">
                  <p>Failed to load video metrics</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .youtube-analytics-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .analytics-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .analytics-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
        }

        /* Controls */
        .analytics-controls {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 200px;
        }

        .search-input {
          width: 100%;
          padding: 10px 16px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .search-input:focus {
          outline: none;
          border-color: #FF0000;
          box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.1);
        }

        .sort-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .sort-controls label {
          font-size: 0.875rem;
          color: #6b7280;
        }

        .sort-select {
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .btn-icon {
          padding: 8px 12px;
          font-size: 1.25rem;
          line-height: 1;
        }

        /* Videos grid */
        .videos-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
          margin-bottom: 24px;
        }

        .video-card {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          cursor: pointer;
          transition: all 0.2s;
        }

        .video-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }

        .video-thumbnail {
          position: relative;
          width: 100%;
          padding-top: 56.25%; /* 16:9 aspect ratio */
          background: #f3f4f6;
        }

        .video-thumbnail img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .no-thumbnail {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9ca3af;
          font-size: 0.875rem;
        }

        .video-duration {
          position: absolute;
          bottom: 8px;
          right: 8px;
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        .privacy-badge {
          position: absolute;
          top: 8px;
          left: 8px;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .privacy-badge.public {
          background: #d1fae5;
          color: #065f46;
        }

        .privacy-badge.unlisted {
          background: #fef3c7;
          color: #78350f;
        }

        .privacy-badge.private {
          background: #fee2e2;
          color: #991b1b;
        }

        .video-info {
          padding: 16px;
        }

        .video-title {
          margin: 0 0 8px 0;
          font-size: 0.9375rem;
          font-weight: 600;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .video-date {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #6b7280;
          font-size: 0.8125rem;
          margin-bottom: 12px;
        }

        .video-stats {
          display: flex;
          gap: 16px;
        }

        .stat {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #6b7280;
          font-size: 0.8125rem;
        }

        /* Loading and empty states */
        .loading-state,
        .empty-state,
        .not-connected {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f4f6;
          border-top-color: #FF0000;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-icon,
        .not-connected-icon {
          font-size: 4rem;
          margin-bottom: 16px;
        }

        .empty-state h3,
        .not-connected h3 {
          margin: 0 0 8px 0;
          font-size: 1.125rem;
          font-weight: 600;
        }

        .empty-state p,
        .not-connected p {
          margin: 0;
          color: #6b7280;
        }

        /* Load more */
        .load-more-container {
          display: flex;
          justify-content: center;
          margin-top: 24px;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .modal-close {
          background: none;
          border: none;
          font-size: 2rem;
          line-height: 1;
          cursor: pointer;
          color: #6b7280;
          padding: 0;
          width: 32px;
          height: 32px;
        }

        .modal-close:hover {
          color: #111827;
        }

        .modal-body {
          padding: 20px;
        }

        .video-preview {
          margin-bottom: 24px;
          border-radius: 8px;
          overflow: hidden;
        }

        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 16px;
          margin-bottom: 24px;
        }

        .metric-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
        }

        .metric-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
        }

        .metric-icon.views {
          background: #dbeafe;
          color: #1e40af;
        }

        .metric-icon.likes {
          background: #fce7f3;
          color: #be185d;
        }

        .metric-icon.comments {
          background: #d1fae5;
          color: #065f46;
        }

        .metric-icon.favorites {
          background: #fef3c7;
          color: #78350f;
        }

        .metric-details {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .metric-label {
          font-size: 0.75rem;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .metric-value {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
        }

        .video-details-section {
          margin-bottom: 24px;
        }

        .video-details-section h4 {
          margin: 0 0 12px 0;
          font-size: 1rem;
          font-weight: 600;
        }

        .detail-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .detail-row:last-child {
          border-bottom: none;
        }

        .detail-label {
          font-weight: 500;
          color: #6b7280;
          min-width: 120px;
        }

        .video-description {
          margin: 0;
          padding: 12px;
          background: #f9fafb;
          border-radius: 6px;
          line-height: 1.6;
          color: #374151;
          font-size: 0.875rem;
          white-space: pre-wrap;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        /* Alert */
        .alert {
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 20px;
          position: relative;
        }

        .alert-error {
          background: #fee2e2;
          color: #991b1b;
          border: 1px solid #fca5a5;
        }

        .alert-close {
          position: absolute;
          top: 8px;
          right: 8px;
          background: none;
          border: none;
          font-size: 1.5rem;
          line-height: 1;
          cursor: pointer;
          color: inherit;
          opacity: 0.7;
        }

        .alert-close:hover {
          opacity: 1;
        }

        /* Buttons */
        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 10px 20px;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          text-decoration: none;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: #FF0000;
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          background: #CC0000;
        }

        .btn-secondary {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-secondary:hover:not(:disabled) {
          background: #e5e7eb;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .videos-grid {
            grid-template-columns: 1fr;
          }

          .metrics-grid {
            grid-template-columns: 1fr;
          }

          .analytics-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .sort-controls {
            justify-content: space-between;
          }

          .modal-actions {
            flex-direction: column;
          }

          .btn {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

export default YouTubeAnalytics;
